import { AfterViewInit, Component, EnvironmentInjector, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { ReplaySubject, Subject, combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';

import { App } from '@capacitor/app';
import { KeyboardInfo } from '@capacitor/keyboard';
import { Preferences } from '@capacitor/preferences';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

import { ActionSheetController, AlertController, ModalController, Platform } from '@ionic/angular';

import { environment } from '@env/environment';
import { PushNotificationSchema } from '@metib/push-notifications';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import { Base64 } from 'js-base64';
import { register } from 'swiper/element/bundle';

import { AlertService } from '@core/services/alert/alert.service';
import { ApiUrlService } from '@core/services/api/api-url.service';
import { ApiService } from '@core/services/api/api.service';
import { B2pService } from '@core/services/b2p/b2p.service';
import { BatteryOptimizationService } from '@core/services/battery-optimization/battery-optimization.service';
import { DeepLinkService } from '@core/services/deep-link/deep-link.service';
import { DeviceService } from '@core/services/device/device.service';
import { HostFailoverService } from '@core/services/host-failover/host-failover.service';
import { LoadingService } from '@core/services/loading/loading.service';
import { LogoutService } from '@core/services/logout/logout.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { PushNotificationsService } from '@core/services/push-notifications/push-notifications.service';
import { QrPaymentHandlerService } from '@core/services/qr-scanner/qr-payment-handler.service';
import { QrScannerService } from '@core/services/qr-scanner/qr-scanner.service';

import { OtaUpdatesService } from '@pages/other/ota-updates/services/ota-updates.service';

import { AuthService } from '@shared/services/auth.service';
import { NetworkService } from '@shared/services/network.service';

require('dayjs/locale/ru');

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.locale('ru');

register();

@UntilDestroy()
@Component({
  selector: 'app-app',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  unsubscribe: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  networkStatus = 0;
  activeRoute = '';
  lightTheme = '#f6f8fb';
  darkTheme = '#1F1F21';

  private reauthenticateAction$ = this.logoutService.reauthenticate$.pipe(
    tap((shouldReauthenticate) => {
      if (shouldReauthenticate) {
        this.reauthenticate();
      }
    })
  );

  private destroyPushAndDeeplinkHandler$ = new Subject();

  @HostListener('window:keyboardWillShow', ['$event'])
  onKeyboardShow(e: KeyboardInfo) {
    if (document.activeElement === document.body || document.activeElement === document.documentElement) {
      const keyboard = (window as any).Keyboard;
      if (keyboard && keyboard.hide) {
        this.deviceService.setKeyboardHeight(0);
        return keyboard.hide();
      }
    }
    this.deviceService.setKeyboardHeight(e.keyboardHeight);
    this.toggleKeyboardState(true);
  }

  @HostListener('window:keyboardWillHide', ['$event'])
  onKeyboarHide() {
    this.deviceService.setKeyboardHeight(0);
    this.toggleKeyboardState(false);
  }

  constructor(
    public readonly environmentInjector: EnvironmentInjector,
    private platform: Platform,
    private deviceService: DeviceService,
    private authService: AuthService,
    private modalCtrl: ModalController,
    private router: Router,
    private qrScannerService: QrScannerService,
    private b2pService: B2pService,
    private deepLinkService: DeepLinkService,
    private pushNotificationsService: PushNotificationsService,
    private apiUrlService: ApiUrlService,
    private qrPaymentHandlerService: QrPaymentHandlerService,
    private alertService: AlertService,
    private loadingService: LoadingService,
    private networkService: NetworkService,
    private apiService: ApiService,
    private actionSheetCtrl: ActionSheetController,
    private alertController: AlertController,
    private navigationService: NavigationService,
    private batteryOptimizationService: BatteryOptimizationService,
    private logoutService: LogoutService,
    private otaUpdatesService: OtaUpdatesService,
    private hostFailoverService: HostFailoverService
  ) {
    platform.ready().then(async () => {
      this.deepLinkService.addListener();

      // Настройка автоматического logout
      this.configurePauseResumeAction();

      await this.deviceService.detectIphoneX();

      if (DeviceService.isDevice) {
        await this.watchThemeChanges();
        await this.setStatusBarColor();
      }

      // Настройка обработки нативной кнопки назад
      this.setNativeBackButtonHandler();

      await SplashScreen.hide();
      void this.batteryOptimizationService.disableOptimizationWhenEnabled();
    });
  }

  ngOnInit() {
    this.initApp();
  }

  ngAfterViewInit() {
    const closeQrElem = document.getElementById('closeQr');
    const scanFromFileButton = document.getElementById('scanFromFile');
    closeQrElem.addEventListener('click', () => {
      this.qrScannerService.stopScan();
    });
    scanFromFileButton.addEventListener('click', () => {
      this.qrScannerService
        .scanFromImage()
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (result) => {
            this.qrScannerService.stopScan();
            if (this.authService.checkIsAuthorized()) {
              this.qrPaymentHandlerService.handle(result);
            } else {
              this.apiUrlService.setUrl(Base64.decode(result.base64Url));
            }
          },
          error: (error) => {
            this.qrScannerService.stopScan();
            this.alertService.showErrorAlert('Что-то пошло не так');
          }
        });
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
    this.hostFailoverService.destroy();
    this.networkService.removeListener();
    App.removeAllListeners();
  }

  closeB2P() {
    this.b2pService.closeB2P();
  }

  private async configureRequestHost() {
    const result = await Preferences.get({ key: 'API_URL' });
    const host = result.value ? result.value : atob(environment.host);
    let parsedHost = this.apiUrlService.removeLastSlash(host);

    const isNative = DeviceService.isDevice;
    parsedHost = parsedHost === 'https://online.metib.ru' && isNative ? 'https://onlinelocal.metib.ru' : parsedHost;

    await this.apiUrlService.setUrl(parsedHost);
    this.pushNotificationsService.setUrl(parsedHost);
  }

  private initializeHostfailoverService() {
    void firstValueFrom(this.hostFailoverService.initialize());
  }

  private setRootAuthPage(): void {
    this.authService
      .canEnterPin()
      .pipe(untilDestroyed(this))
      .subscribe(({ canEnterPin, useFingerprint, autoLogin }) => {
        if (canEnterPin) {
          void this.navigationService.setRoot('pin', { queryParams: { mode: 'auth', useFingerprint, autoLogin } });
        } else {
          void this.navigationService.setRoot('login');
        }
      });
  }

  private async reauthenticate() {
    this.authService.setIsAuthorized(false);
    await this.qrScannerService.stopScan();
    this.closeB2P();
  }

  toggleKeyboardState(state: boolean) {
    if (state) {
      document.querySelector('ion-app').classList.add('keyboard-open');
    } else {
      document.querySelector('ion-app').classList.remove('keyboard-open');
    }
  }

  private setNativeBackButtonHandler() {
    this.platform.backButton.subscribeWithPriority(10, async (processNextHandler) => {
      const currentUrl = this.router.url;
      const segments = currentUrl.split('/').filter((segment) => segment.length > 0);
      const topModal = await this.modalCtrl.getTop();
      const topActionSheet = await this.actionSheetCtrl.getTop();
      const topAlert = await this.alertController.getTop();

      // 1. Закрыть лоадер, если открыт
      if (this.loadingService.loaderExist) void this.loadingService.showLoader(false);

      // 2. Закрыть QR scanner, если открыт
      if (this.qrScannerService.isScanning) {
        void this.qrScannerService.stopScan();
      }

      // 3. Закрыть открытый алерт (кроме указанных страниц)
      if (this.alertService.activeAlert || topAlert) {
        if (['pin'].indexOf(this.activeRoute) != -1) {
          return;
        }
        if (this.alertService.activeAlert) await this.alertService.dismiss();
        if (topAlert) await topAlert.dismiss();
        return;
      }

      // 4. Закрыть открытый Action Sheet, если есть
      else if (topActionSheet) {
        await this.alertService.dismiss();
        return;
      }

      // 5. Закрыть открытую модалку, если есть
      else if (topModal) {
        if (topModal.backdropDismiss) {
          await topModal.dismiss();
        }

        return;
      }

      // 6. Проверяем, есть ли открытый iFrame платежа с Best2Pay
      else if (this.b2pService.isB2POpen) {
        this.closeB2P();
        return;
      }

      // 7. Свернуть приложение в трей, если пользователь находится на корневой странице стека
      else if (segments.length === 1) {
        const currentRoute = segments[0]; // Получаем текущий маршрут

        if (currentRoute !== 'main') {
          // Если не на странице 'main', перенаправляем на 'main'
          await this.navigationService.setRoot('/main');
        } else {
          // Если уже на странице 'main', показываем подтверждение выхода
          await this.alertService.showAlert({
            header: 'Вы хотите выйти из приложения?',
            message: '',
            buttons: [
              {
                text: 'Да',
                handler: () => {
                  App.exitApp();
                }
              },
              {
                text: 'Нет',
                role: 'cancel'
              }
            ]
          });
        }
        return;
      }

      // n. Исключения для определённых страниц и стандартная обработка
      else {
        // n.1. /payments/sbp-qr
        // - Всегда должна возвращать на /main
        if (this.router.url.split('?')[0] === '/payments/sbp-qr') {
          await this.navigationService.setRoot('/main');
          return;
        }

        // n.2. /payments/sbp-details
        // - Всегда должна возвращать на /main
        if (this.router.url.split('?')[0] === '/payments/sbp-details') {
          this.navigationService.setRoot('/payments');
          return;
        }

        // n.x. Стандартная обработка
        processNextHandler();
      }
    });
  }

  private configurePauseResumeAction() {
    this.logoutService.startTimer();
    this.logoutService.runCounterResetOnUserInteraction();
    this.logoutService.runPauseAndResumeEventHandler();

    this.reauthenticateAction$.pipe().subscribe();
  }

  private async setStatusBarColor(): Promise<void> {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (DeviceService.isAndroid) {
      await StatusBar.setStyle({
        style: Style.Dark
      });
      await StatusBar.setBackgroundColor({
        color: this.darkTheme
      });
    }

    if (DeviceService.isIos) {
      await StatusBar.setStyle({
        style: Style.Light
      });
      if (this.deviceService.osVersion <= 15) {
        await StatusBar.hide();
        await new Promise((resolve) => setTimeout(resolve, 500));
        await StatusBar.setStyle({
          style: Style.Light
        });
      }
    }
  }

  private async watchThemeChanges() {
    window.matchMedia('(prefers-color-scheme: dark)').addListener(() => {
      this.setStatusBarColor();
    });
  }

  private async initApp() {
    // Конфигурируем хост для HttpClient
    await this.configureRequestHost();
    this.listenersOtaUpdate();
    this.initializeHostfailoverService();
    await this.otaUpdatesService.configureChannel();

    // Начинаем проверять доступность сети
    this.networkService.listenNetworkStatus();

    this.apiUrlService
      .getUrl()
      .pipe(
        filter((url) => Boolean(url)),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe((url) => this.pushNotificationsService.setUrl(url));

    this.authService.isAuthorized$.pipe(untilDestroyed(this)).subscribe({
      next: (isAuth) => {
        if (isAuth) {
          this.navigationService.setRoot('main').then(() => {
            this.createPushAndDeeplinkHandler();
            this.deviceService.appInForeground$.next(false);
          });
        } else {
          this.destroyPushAndDeeplinkHandler$.next(true);
          this.setRootAuthPage();
        }
      },
      error: (err) => {}
    });
  }

  private listenersOtaUpdate() {
    this.otaUpdatesService.downloadComplete$.pipe(untilDestroyed(this)).subscribe({
      next: (completed) => {
        this.deviceService.showApplicationUpdateAlert().then((alert) => {
          alert.onWillDismiss().then(({ role }) => {
            switch (role) {
              case 'later':
                this.applyOtaUpdate(true);
                break;
              case 'update':
                this.applyOtaUpdate(false);
                break;
            }
          });
        });
      },
      error: (error) => {}
    });

    this.otaUpdatesService.channelChange$.pipe(untilDestroyed(this)).subscribe({
      next: async () => {
        if (this.otaUpdatesService.isDownloading) {
          return;
        }
        this.otaUpdatesService.isDownloading = true;
        try {
          const updateInfo = await this.otaUpdatesService.checkForUpdates();
          if (updateInfo.isAvailable && updateInfo.availableType !== 'MAJOR') {
            await this.otaUpdatesService.downloadUpdate(updateInfo.version, updateInfo.filename, updateInfo.checksum);
          }
        } catch (e) {
          this.otaUpdatesService.logger.error('Ошибка обновления', e as Error);
        } finally {
          this.otaUpdatesService.isDownloading = false;
        }
      }
    });
  }

  private createPushAndDeeplinkHandler() {
    combineLatest([
      this.deviceService.appInForeground$,
      this.deepLinkService.deeplinkTriggered$,
      this.pushNotificationsService.pushNotificationActionPerformed$
    ])
      .pipe(takeUntil(this.destroyPushAndDeeplinkHandler$))
      .subscribe(([appInForeground, deepLinkTriggered, pushSchema]) => {
        if (!appInForeground) {
          if (deepLinkTriggered) {
            this.deepLinkService.handleDeeplink();
          }
          if (pushSchema) {
            this.handlePushPerformed(pushSchema);
          }
        }
      });
  }

  private handlePushPerformed(pushSchema: PushNotificationSchema) {
    if (pushSchema.appUrl) {
      this.deepLinkService.setUrl(pushSchema.appUrl);
      this.deepLinkService.handleDeeplink(false);
      this.pushNotificationsService.clearActionPerformed();
      return;
    }
    this.navigationService.setRoot('other/notifications').then(() => {
      this.pushNotificationsService.clearActionPerformed();
    });
  }

  private async applyOtaUpdate(onNextLaunch: boolean) {
    if (onNextLaunch) {
      this.otaUpdatesService.applyUpdate(onNextLaunch);
    } else {
      await SplashScreen.show({
        autoHide: false
      });
      try {
        await this.otaUpdatesService.applyUpdate(onNextLaunch);
      } catch (e) {
        SplashScreen.hide();
      }
    }
  }
}
